// Email+password login via Supabase Auth.
import { useState } from 'react';

import { supabase } from '../api/supabase';
import { setSupabaseToken } from '../api/client';
import Logo from '../components/brand/Logo';
import type { StudentProfile } from '../api/types';

interface Props {
  onLogin: (profile: StudentProfile) => void;
  onForgotPassword: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function LoginScreen({ onLogin: _onLogin, onForgotPassword }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!email.trim() || !password) {
        setError('Bitte Email und Passwort eingeben.');
        return;
      }
      const { data, error: authErr } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (authErr || !data.session) {
        if (authErr?.message?.includes('Email not confirmed')) {
          setError('Bitte erst den Bestätigungslink in deiner Email anklicken.');
        } else if (authErr?.message?.includes('Invalid login')) {
          setError('Email oder Passwort ist falsch.');
        } else {
          setError(authErr?.message ?? 'Login fehlgeschlagen.');
        }
        return;
      }
      setSupabaseToken(data.session.access_token);
      // The Supabase SIGNED_IN event fires automatically; App.tsx routes from
      // there (to /story on success, /complete_registration if the family
      // record isn't set up yet). Don't call getProfileV3 here — doing so
      // races the auth handler and can briefly flash a "Kein Konto" error.
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shellStyle}>
      <div style={{ width: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28 }}>
        {/* Logo — hero placement, larger than in-app surfaces. */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <Logo variant="lockup" height={140} />
          <div style={subtitleStyle}>Lernabenteuer · Grundschule</div>
        </div>

        {/* Pitch card */}
        <div style={pitchCardStyle}>
          <div style={eyebrowStyle}>Was ist Finnly?</div>
          Ein Lernabenteuer, in dem Grundschulkinder (Klasse 1–4) beim Lernen Finn dem Fuchs
          helfen. Mathe, Deutsch, HSU und Musik — als Geschichte, nicht als Arbeitsblatt.
        </div>

        <div style={fieldColStyle}>
          <label style={fieldLabelStyle} htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            disabled={loading}
            placeholder="du@beispiel.de"
            style={textInputStyle(!!error)}
          />
          <label style={fieldLabelStyle} htmlFor="login-password">Passwort</label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            disabled={loading}
            style={textInputStyle(!!error)}
          />
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <button onClick={submit} disabled={loading} style={primaryBtnStyle(loading)}>
          {loading ? 'Einen Moment…' : 'Einloggen →'}
        </button>

        <div style={secondaryRowStyle}>
          <button onClick={onForgotPassword} style={linkBtnStyle}>Passwort vergessen?</button>
        </div>

        <div style={footerStyle}>
          <a href="/eltern-anleitung.html" style={footerLinkStyle} target="_blank" rel="noopener">Eltern-Anleitung</a>
          <span aria-hidden="true" style={{ opacity: 0.4 }}>·</span>
          <a href="/faq.html" style={footerLinkStyle} target="_blank" rel="noopener">Häufige Fragen</a>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const shellStyle: React.CSSProperties = {
  display: 'flex', height: '100dvh',
  alignItems: 'center', justifyContent: 'center',
  background: 'var(--parchment)', fontFamily: 'var(--sans)',
};
const subtitleStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.22em',
  color: 'var(--ink-muted)', textTransform: 'uppercase', marginTop: 6,
};
const pitchCardStyle: React.CSSProperties = {
  width: '100%', padding: '16px 18px', background: 'var(--card)',
  border: '1px solid rgba(60,50,40,0.12)', borderRadius: 4,
  fontFamily: 'var(--serif)', fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5,
};
const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
  color: 'var(--amber-deep)', textTransform: 'uppercase', marginBottom: 6,
};
const fieldColStyle: React.CSSProperties = {
  width: '100%', display: 'flex', flexDirection: 'column', gap: 6,
};
const fieldLabelStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.2em',
  color: 'var(--ink-muted)', textTransform: 'uppercase', marginTop: 6,
};
const textInputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%', padding: '12px 14px', background: 'var(--card)',
  border: `1.5px solid ${hasError ? 'var(--wrong)' : 'rgba(60,50,40,0.2)'}`,
  borderRadius: 4, fontFamily: 'var(--body)', fontSize: 15,
  color: 'var(--ink)', outline: 'none', boxSizing: 'border-box',
});
const errorStyle: React.CSSProperties = {
  width: '100%', fontFamily: 'var(--mono)', fontSize: 11,
  color: 'var(--wrong)', letterSpacing: '0.04em',
};
const primaryBtnStyle = (loading: boolean): React.CSSProperties => ({
  width: '100%', padding: '16px',
  background: loading ? 'rgba(60,50,40,0.1)' : 'var(--amber)',
  color: loading ? 'var(--ink-muted)' : '#fff',
  border: 'none', borderRadius: 3, fontFamily: 'var(--sans)',
  fontSize: 16, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
  boxShadow: loading ? 'none' : '0 4px 14px rgba(201,106,47,0.35)',
  transition: 'all 0.15s',
});
const secondaryRowStyle: React.CSSProperties = {
  display: 'flex', gap: 18, alignItems: 'center',
};
const linkBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', fontFamily: 'var(--mono)',
  fontSize: 10, letterSpacing: '0.18em', color: 'var(--ink-muted)',
  textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'underline',
};
const footerStyle: React.CSSProperties = {
  display: 'flex', gap: 18, alignItems: 'center', fontFamily: 'var(--mono)',
  fontSize: 10, letterSpacing: '0.14em', color: 'var(--ink-muted)',
  textTransform: 'uppercase',
};
const footerLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };
